import {Scope} from 'Shared/constants/Scope';
import {all, call, put, takeEvery, takeLatest} from '@redux-saga/core/effects';
import AgencyAdministrationRouteConstants from 'AgencyAdministration/AgencyAdministrationRouteConstants';
import {goBack, push} from 'connected-react-router';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult, fromResult, Loading, LoadingAdditional, ServerError} from 'Core/models/Async';
import {ValidEmail} from 'Core/models/Email';
import {NonEmptyString} from 'Core/models/EmptyString';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import {isSome} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {map as mapTaskEither} from 'fp-ts/lib/TaskEither';
import {STANDARD_ERROR} from 'Shared/components/Alert';
import {refreshToken} from 'Shared/utilities/Auth';
import {LocalStorageKey, removeFromLocalStorage} from 'Shared/utilities/localStorage';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {getApiCallGenerator} from 'Shared/utilities/sagaApiGenerators';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import userApi from 'Users/api/userApi';
import {
  changeIndpendentAgent,
  changeLinkAgency,
  changePreregisteredAgents,
  changePreregisteredUser,
  changeSecurityQuestions,
  deletePreregistration,
  fetchIndependentAgent,
  getPreregisteredAgents,
  getPreregisteredUser,
  IndependentAgentActionType,
  linkAgency,
  LinkAgentState,
  loadIndependentAgent,
  preregisterAgent,
  resendEmail,
} from 'Users/ducks/independentAgent';
import {setSelectedAgency, setUserClaims} from 'Users/ducks/userProfile';
import {
  IndependentAgentRegistrationWithError,
  SavingIndependentAgentRegistration,
  ValidIndependentAgentRegistration,
} from 'Users/models/IndependentAgent';
import {PreregisteredAgent} from 'Users/models/PreregisteredAgent';
import {extractApplicationIdsFromScopeSaga} from './applicationIdSaga';
import {Application} from 'Users/models/Application';
import {findFirst, head} from 'fp-ts/lib/Array';
import {UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {eq} from 'Shared/utilities/equals';
import * as O from 'fp-ts/lib/Option';
import {getIsNewYorkFromAgency} from 'Shared/utilities/isNewYorkUtility';

export function getIndependentAgent(action: ReturnType<typeof fetchIndependentAgent>) {
  return pipe(
    () => userApi.getUser(action.payload.userId),
    mapTaskEither(user =>
      ValidIndependentAgentRegistration.create(
        NonEmptyString.create(user.firstName),
        NonEmptyString.create(user.lastName),
        ValidEmail.create(user.email),
        user.agencyAssociations.map(agency => agency),
        []
      )
    )
  )();
}

export function* fetchIndependentAgentSaga() {
  yield takeLatest(
    IndependentAgentActionType.FETCH_AGENT,
    getApiCallGenerator(getIndependentAgent, loadIndependentAgent)
  );
}

export const removeIARegistrationKeyFromLocalStorage = removeFromLocalStorage(
  LocalStorageKey.IndependentAgentRegistrationKey
);

export function* loadNewUserClaims() {
  const newClaims = getUserClaims();

  if (isSome(newClaims)) {
    const agentClaims = pipe(
      newClaims.value.userTypes,
      findFirst(eq('$type', UserTypeClaimKey.AgentSite)),
      O.chain(
        UserTypeClaims.match({
          [UserTypeClaimKey.AgentSite]: agentClaims => {
            const impersonatedAgentClaims = O.fromNullable(agentClaims.impersonatedAgentClaim);
            return O.isSome(impersonatedAgentClaims) ? impersonatedAgentClaims : O.some(agentClaims);
          },
          default: () => O.none,
        })
      )
    );

    if (O.isSome(agentClaims)) {
      const defaultAgency = pipe(agentClaims.value.agencyAssociations, head);
      const isNewYork = getIsNewYorkFromAgency(defaultAgency);

      yield put(setSelectedAgency(defaultAgency, isNewYork));
    }
    yield put(setUserClaims(fromResult(newClaims.value)));
  }
}

export function* linkAgentToAgency(action: ReturnType<typeof linkAgency>) {
  try {
    yield call(removeIARegistrationKeyFromLocalStorage);
    yield call(userApi.linkAgentToAgency, action.payload.username, action.payload.userKey);
    yield call(refreshToken);

    const newClaims = getUserClaims();
    if (isSome(newClaims)) {
      const agentClaims = pipe(
        newClaims.value.userTypes,
        findFirst(eq('$type', UserTypeClaimKey.AgentSite)),
        O.chain(
          UserTypeClaims.match({
            [UserTypeClaimKey.AgentSite]: agentClaims => {
              const impersonatedAgentClaims = O.fromNullable(agentClaims.impersonatedAgentClaim);
              return O.isSome(impersonatedAgentClaims) ? impersonatedAgentClaims : O.some(agentClaims);
            },
            default: () => O.none,
          })
        )
      );
      if (O.isSome(agentClaims)) {
        const defaultAgency = pipe(agentClaims.value.agencyAssociations, head);

        const isNewYork = getIsNewYorkFromAgency(defaultAgency);
        yield put(setSelectedAgency(defaultAgency, isNewYork));
      }
      yield put(setUserClaims(fromResult(newClaims.value)));
    }

    yield put(push(CoreRouteConstants.DASHBOARD));
  } catch (e) {
    yield put(changeLinkAgency(LinkAgentState.linkingError(new ServerError('An error ocurred while trying to link.'))));
  }
}

export function* linkAgencySaga() {
  yield takeEvery(IndependentAgentActionType.LINK_AGENCY, linkAgentToAgency);
}

export function* loadPreregisteredUser(action: ReturnType<typeof getPreregisteredUser>) {
  yield getApiCallGenerator(
    () => userApi.getPreregistrationUser(action.payload.preregisteredUserKey),
    changePreregisteredUser
  )(action);
}

export function* getPreregisteredUserSaga() {
  yield takeEvery(IndependentAgentActionType.GET_PREREGISTERED_USER, loadPreregisteredUser);
}

export function* preregisterAgentApi(action: ReturnType<typeof preregisterAgent>) {
  const {encryptedIdentifier, registration} = action.payload;

  yield put(changeIndpendentAgent(fromResult(SavingIndependentAgentRegistration.create(registration))));

  const result: AsyncResult<unknown> = yield call(
    userApi.preregisterAgent,
    [Application.Agent],
    registration,
    encryptedIdentifier
  );

  if (E.isRight(result)) {
    yield put(goBack());
  } else {
    if (result.left instanceof ServerError) {
      yield put(
        changeIndpendentAgent(
          fromResult(
            IndependentAgentRegistrationWithError.create(registration, result.left.errorDetails || STANDARD_ERROR)
          )
        )
      );
    } else {
      yield put(
        changeIndpendentAgent(
          fromResult(IndependentAgentRegistrationWithError.create(registration, result.left.message))
        )
      );
    }
  }
}

export function* preregisterAgentSaga() {
  yield takeEvery(IndependentAgentActionType.PREREGISTER_AGENT, preregisterAgentApi);
}

export function* sendRegistrationEmailApi(action: ReturnType<typeof resendEmail>) {
  const {encryptedIdentifier, userId} = action.payload;
  const result: AsyncResult<unknown> = yield call(userApi.resendPreregistrationEmail, userId, encryptedIdentifier);

  if (E.isRight(result)) {
    yield put(createSnackbar(`Email Sent`));
  } else {
    yield put(createSnackbar(`An Error Ocurred While Resending the Email.`));
  }
}

export function* resendRegistrationEmailSaga() {
  yield takeEvery(IndependentAgentActionType.RESEND_EMAIL, sendRegistrationEmailApi);
}

export function* deletePreregistrationApiSaga(action: ReturnType<typeof deletePreregistration>) {
  const {encryptedIdentifier, userId, filter} = action.payload;
  const applicationIds = yield extractApplicationIdsFromScopeSaga(Scope.DeletePreregistrationUser);
  const result = yield call(userApi.deletePreregistration, userId, applicationIds, encryptedIdentifier);

  yield put(getPreregisteredAgents(filter, undefined, encryptedIdentifier));
}

export function* deletePreregistrationSaga() {
  yield takeEvery(IndependentAgentActionType.DELETE_PREREGISTRATION, deletePreregistrationApiSaga);
}

export function* getSecurityQuestionsSaga() {
  yield takeEvery(
    IndependentAgentActionType.GET_SECURITY_QUESTIONS,
    getApiCallGenerator(userApi.getSecurityQuestions, changeSecurityQuestions)
  );
}

export function* fetchPreregisteredAgents(action: ReturnType<typeof getPreregisteredAgents>) {
  const {filter, page, encryptedIdentifier} = action.payload;
  if (action.payload.page) {
    yield put(changePreregisteredAgents(new LoadingAdditional(action.payload.page)));
  } else {
    yield put(changePreregisteredAgents(new Loading(action.payload.page)));
  }

  // This is hardcoded to Agent since this page is Agent only
  const applicationIds = [Application.Agent];
  const result: AsyncResult<Page<PreregisteredAgent>> = yield page
    ? call(mergeWithNextPage, {existingPage: page})
    : call(userApi.getPreregisteredUsers, filter, applicationIds, encryptedIdentifier);

  yield pipe(
    result,
    E.map(fromResult),
    E.fold(
      value => put(changePreregisteredAgents(value)),
      value => put(changePreregisteredAgents(value))
    )
  );
}

export function* listPreregisteredAgentsSaga() {
  yield takeEvery(IndependentAgentActionType.FETCH_PREREGISTERED_AGENTS, fetchPreregisteredAgents);
}

export default function*() {
  yield all([
    fetchIndependentAgentSaga(),
    preregisterAgentSaga(),
    linkAgencySaga(),
    listPreregisteredAgentsSaga(),
    resendRegistrationEmailSaga(),
    getSecurityQuestionsSaga(),
    getPreregisteredUserSaga(),
    deletePreregistrationSaga(),
  ]);
}
